// Product of Launch Maniac LLC, Las Vegas, Nevada - (725) 444-8200 support@launchmaniac.com

import { createRouter, createWebHistory } from 'vue-router';
import type { RouteRecordRaw } from 'vue-router';
import { useAuthStore } from './stores/auth';

const routes: RouteRecordRaw[] = [
  // Public
  {
    path: '/login',
    name: 'login',
    component: () => import('./views/Login.vue'),
    meta: { public: true },
  },
  {
    path: '/setup',
    name: 'setup',
    component: () => import('./views/SetupWizard.vue'),
    meta: { public: true },
  },

  // Main
  {
    path: '/',
    name: 'overview',
    component: () => import('./views/Overview.vue'),
  },

  // CRM
  {
    path: '/contacts',
    name: 'contacts',
    component: () => import('./views/Contacts.vue'),
  },
  {
    path: '/contacts/import',
    name: 'contact-import',
    component: () => import('./views/ContactImport.vue'),
  },
  {
    path: '/contacts/:id',
    name: 'contact-detail',
    component: () => import('./views/ContactDetail.vue'),
  },
  {
    path: '/conversations',
    name: 'conversations',
    component: () => import('./views/Conversations.vue'),
  },

  // Campaigns
  {
    path: '/cohort-builder',
    name: 'cohort-builder',
    component: () => import('./views/CohortBuilder.vue'),
  },
  {
    path: '/segments',
    name: 'segments',
    component: () => import('./views/SegmentsExperiments.vue'),
  },
  {
    path: '/composer',
    name: 'composer',
    component: () => import('./views/CampaignComposer.vue'),
  },
  {
    path: '/cadence',
    name: 'cadence',
    component: () => import('./views/CadenceEngine.vue'),
  },
  {
    path: '/launch-control',
    name: 'launch-control',
    component: () => import('./views/LaunchControl.vue'),
  },

  // Operations
  {
    path: '/live-triage',
    name: 'live-triage',
    component: () => import('./views/LiveTriage.vue'),
  },
  {
    path: '/hygiene',
    name: 'hygiene',
    component: () => import('./views/HygieneRedFlags.vue'),
  },
  {
    path: '/exceptions',
    name: 'exceptions',
    component: () => import('./views/ExceptionsQueue.vue'),
  },

  // Billing
  {
    path: '/ledger',
    name: 'ledger',
    component: () => import('./views/Ledger.vue'),
  },
  {
    path: '/weekly-close',
    name: 'weekly-close',
    component: () => import('./views/WeeklyClose.vue'),
  },
  {
    path: '/disputes',
    name: 'disputes',
    component: () => import('./views/DisputesCredits.vue'),
  },

  // Admin
  {
    path: '/suppressions',
    name: 'suppressions',
    component: () => import('./views/SuppressionCompliance.vue'),
  },
  {
    path: '/admin/messaging',
    name: 'admin-messaging',
    component: () => import('./views/MessagingSettings.vue'),
    meta: { adminOnly: true },
  },
  {
    path: '/admin/a2p',
    name: 'admin-a2p',
    component: () => import('./views/TwilioA2PCenter.vue'),
    meta: { adminOnly: true },
  },
  {
    path: '/settings',
    name: 'settings',
    component: () => import('./views/SettingsAudit.vue'),
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

router.beforeEach(async (to) => {
  if (to.meta.public) return true;

  const auth = useAuthStore();

  // Always check first-boot status (works without token)
  if (!auth.user) await auth.fetchMe();

  if (auth.user?.first_boot && to.name !== 'setup') {
    return { name: 'setup' };
  }

  const token = localStorage.getItem('repipe_token');
  if (!token) return { name: 'login' };

  if (!auth.isAuthenticated) return { name: 'login' };

  if (to.meta.adminOnly && auth.userRole !== 'ADMIN') {
    return { name: 'overview' };
  }

  return true;
});

export default router;
